import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendSupportEmail, SupportEmailData } from '../../../api/productService';
import './ContactForm.css';

interface ContactFormData {
  name: string;
  email: string;
  concern: string;
  details: string;
}

const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    concern: '',
    details: ''
  });
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.email.trim()) {
      newErrors.email = t('contact.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.email_invalid');
    }

    if (!formData.concern.trim()) {
      newErrors.concern = t('contact.concern_required');
    }

    if (!formData.details.trim()) {
      newErrors.details = t('contact.details_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Map form data to API payload structure
      const supportData: SupportEmailData = {
        email: formData.email,
        name: formData.name,
        topic: formData.concern, // concern maps to topic
        description: formData.details // details maps to description
      };

      // Send support email via API
      await sendSupportEmail(supportData);

      setSubmitSuccess(true);

      // Reset form after success
      setTimeout(() => {
        setFormData({ name: '', email: '', concern: '', details: '' });
        setSubmitSuccess(false);
        navigate('/help');
      }, 2000);

    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrors({ email: t('contact.submit_error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHelp = () => {
    navigate('/help');
  };

  if (submitSuccess) {
    return (
      <div className="contact-form-help-container">
        <div className="contact-form-success">
          <div className="success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#10b981"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="success-title">{t('contact.success_title')}</h2>
          <p className="success-message">{t('contact.success_message')}</p>
          <button className="back-to-help-button" onClick={handleBackToHelp}>
            {t('contact.back_to_help')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-form-help-container">
      <div className="contact-form-card">
        <div className="contact-form-header">
          <h1 className="contact-form-title">{t('contact.title')}</h1>
        </div>

        <form className="contact-form-help" onSubmit={handleSubmit}>
          <div className="form-field">
            <label htmlFor="name" className="form-label">
              {t('contact.name_label')}
            </label>
            <input
              id="name"
              type="text"
              className="form-input"
              placeholder={t('contact.name_placeholder')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="email" className="form-label required">
              {t('contact.email_label')}
            </label>
            <input
              id="email"
              type="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={t('contact.email_placeholder')}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-field">
            <label htmlFor="concern" className="form-label required">
              {t('contact.concern_label')}
            </label>
            <input
              id="concern"
              type="text"
              className={`form-input ${errors.concern ? 'error' : ''}`}
              placeholder={t('contact.concern_placeholder')}
              value={formData.concern}
              onChange={(e) => handleInputChange('concern', e.target.value)}
              required
            />
            {errors.concern && <span className="error-message">{errors.concern}</span>}
          </div>

          <div className="form-field">
            <label htmlFor="details" className="form-label required">
              {t('contact.details_label')}
            </label>
            <textarea
              id="details"
              className={`form-textarea ${errors.details ? 'error' : ''}`}
              placeholder={t('contact.details_placeholder')}
              value={formData.details}
              onChange={(e) => handleInputChange('details', e.target.value)}
              rows={4}
              required
            />
            {errors.details && <span className="error-message">{errors.details}</span>}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('contact.sending') : t('contact.send_button')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
